import { SEO } from '../components/SEO';
import { Shield, Phone } from 'lucide-react';

export const SMSConsent = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="sms-consent-page">
      <SEO
        title="SMS Consent"
        description="Surfside Patrol SMS messaging consent, terms, and opt-out information. Learn about the text messages you may receive and how to manage your preferences."
      />
      <section className="pt-32 pb-24 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518]">
              <Phone className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest">Legal</p>
              <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white font-['Barlow_Condensed']">
                SMS Consent
              </h1>
            </div>
          </div>
          <p className="text-zinc-500 text-sm">
            Last Updated: April 9, 2026
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 text-zinc-400 leading-relaxed">

            {/* Introduction */}
            <div data-testid="sms-intro">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                SMS Messaging <span className="text-[#F5C518]">Terms</span>
              </h2>
              <p className="mb-4">
                Surfside Patrol Private Security ("Surfside Patrol," "we," "us," or "our") offers SMS text messaging services to clients and prospective clients who opt in to receive communications from us. By providing your mobile phone number and consenting to receive text messages, you agree to the following terms.
              </p>
            </div>

            {/* Consent */}
            <div data-testid="sms-consent-section">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Your <span className="text-[#F5C518]">Consent</span>
              </h2>
              <p className="mb-4">
                By opting in to Surfside Patrol's SMS program, you consent to receive text messages from us at the mobile phone number you provided. Your consent is not a condition of purchasing any goods or services. You may opt out at any time.
              </p>
              <p>
                By submitting your phone number through our website contact form, responding to an SMS prompt, or verbally providing consent to a Surfside Patrol representative, you are agreeing to receive SMS messages from Surfside Patrol as described in this policy.
              </p>
            </div>

            {/* Types of Messages */}
            <div data-testid="sms-message-types">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Types of <span className="text-[#F5C518]">Messages</span>
              </h2>
              <p className="mb-4">You may receive the following types of SMS messages from Surfside Patrol:</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Service Notifications</strong> — Updates regarding your active security services, schedule changes, patrol confirmations, and incident alerts.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Appointment Reminders</strong> — Reminders for scheduled consultations, site surveys, system installations, and service appointments.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Security Alerts</strong> — Time-sensitive notifications related to security incidents, alarm activations, or emergency situations on your property.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Quote & Follow-Up Communications</strong> — Responses to your inquiries, quote details, and follow-up messages related to your security needs.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Promotional Messages</strong> — Occasional messages about new services, special offers, or company updates. These are sent only with your explicit consent.</span>
                </li>
              </ul>
            </div>

            {/* Message Frequency */}
            <div data-testid="sms-frequency">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Message <span className="text-[#F5C518]">Frequency</span>
              </h2>
              <p>
                Message frequency varies based on the services you have opted in to and your interaction with Surfside Patrol. Service-related messages are sent as needed based on your active security contract. Promotional messages will not exceed 4 messages per month. You may receive additional messages if you initiate a conversation or request information.
              </p>
            </div>

            {/* Opt-Out */}
            <div data-testid="sms-opt-out" className="bg-[#121212] border border-zinc-800 p-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                How to <span className="text-[#F5C518]">Opt Out</span>
              </h2>
              <p className="mb-4">
                You can opt out of receiving SMS messages at any time by using any of the following methods:
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Reply STOP</strong> — Text STOP to any message you receive from Surfside Patrol. You will receive a one-time confirmation message and no further messages will be sent.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Call Us</strong> — Call <a href="tel:714-588-2460" className="text-[#F5C518] hover:underline">714-588-2460</a> and request to be removed from our SMS list.</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-4 w-4 text-[#F5C518] mr-3 mt-1 flex-shrink-0" />
                  <span><strong className="text-white">Email Us</strong> — Send an email to <a href="mailto:surfside.josh4995@gmail.com" className="text-[#F5C518] hover:underline">surfside.josh4995@gmail.com</a> with "SMS OPT-OUT" in the subject line.</span>
                </li>
              </ul>
              <p className="text-zinc-500 text-sm">
                After opting out, you may still receive a final confirmation message. Opting out of SMS does not affect other communications such as email or phone calls.
              </p>
            </div>

            {/* Help */}
            <div data-testid="sms-help">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Need <span className="text-[#F5C518]">Help?</span>
              </h2>
              <p>
                For assistance with our SMS program, reply <strong className="text-white">HELP</strong> to any message, call us at <a href="tel:714-588-2460" className="text-[#F5C518] hover:underline">714-588-2460</a>, or email <a href="mailto:surfside.josh4995@gmail.com" className="text-[#F5C518] hover:underline">surfside.josh4995@gmail.com</a>.
              </p>
            </div>

            {/* Rates & Charges */}
            <div data-testid="sms-rates">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Message & Data <span className="text-[#F5C518]">Rates</span>
              </h2>
              <p>
                Standard message and data rates may apply depending on your mobile carrier and plan. Surfside Patrol does not charge any additional fees for SMS messages. Contact your wireless carrier for details about your text messaging plan.
              </p>
            </div>

            {/* Supported Carriers */}
            <div data-testid="sms-carriers">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Supported <span className="text-[#F5C518]">Carriers</span>
              </h2>
              <p>
                Our SMS program is supported by all major U.S. wireless carriers including AT&T, Verizon, T-Mobile, Sprint, and most regional carriers. Carriers are not liable for delayed or undelivered messages.
              </p>
            </div>

            {/* Privacy */}
            <div data-testid="sms-privacy">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Privacy & <span className="text-[#F5C518]">Data Use</span>
              </h2>
              <p className="mb-4">
                Your phone number and SMS consent information will be used solely for the purposes described in this policy. We will not sell, rent, or share your mobile phone number or SMS opt-in data with third parties for their marketing purposes. Your information may be shared with our service providers who assist in delivering SMS messages, subject to confidentiality obligations.
              </p>
              <p className="mb-4">
                <strong className="text-white">No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.</strong> All other use of your data is governed by our Privacy Policy.
              </p>
              <p>
                For complete details on how we handle your personal information, please review our{' '}
                <a href="/privacy" className="text-[#F5C518] hover:underline">Privacy Policy</a>.
              </p>
            </div>

            {/* Changes */}
            <div data-testid="sms-changes">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Changes to This <span className="text-[#F5C518]">Policy</span>
              </h2>
              <p>
                Surfside Patrol reserves the right to modify this SMS Consent policy at any time. Any changes will be posted on this page with an updated effective date. Continued participation in our SMS program after changes are posted constitutes your acceptance of the revised terms.
              </p>
            </div>

            {/* Contact */}
            <div data-testid="sms-contact" className="bg-[#121212] border border-[#F5C518]/30 p-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
                Contact <span className="text-[#F5C518]">Information</span>
              </h2>
              <p className="mb-4">If you have any questions about this SMS Consent policy, contact us:</p>
              <div className="space-y-2">
                <p><strong className="text-white">Surfside Patrol Private Security</strong></p>
                <p>2102 Business Center Dr, Irvine, CA 92612</p>
                <p>Phone: <a href="tel:714-588-2460" className="text-[#F5C518] hover:underline">714-588-2460</a></p>
                <p>Email: <a href="mailto:surfside.josh4995@gmail.com" className="text-[#F5C518] hover:underline">surfside.josh4995@gmail.com</a></p>
                <p>PPO #15982</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
